import { useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { countries, getCountry, DEFAULT_COUNTRY } from "../../lib/countries";

/**
 * CountryPhone — composite phone input with a searchable country combobox
 * on the left and a local number input on the right.
 *
 * Props:
 *   country: ISO code string (e.g. "GH"). Default "GH".
 *   phone: local number string (digits only, no dial code).
 *   onChange: ({ country, phone }) => void — called whenever either changes.
 *   testIdPrefix: string used to build data-testid attributes.
 *   placeholder: string for the local number input.
 */
export const CountryPhone = ({
  country = DEFAULT_COUNTRY,
  phone = "",
  onChange,
  testIdPrefix = "phone",
  placeholder = "24 000 0000",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const current = getCountry(country);

  const filtered = query
    ? countries.filter((c) => {
        const q = query.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.code.toLowerCase().includes(q) ||
          c.dial.includes(q.replace(/^\+/, ""))
        );
      })
    : countries;

  const pickCountry = (c) => {
    onChange?.({ country: c.code, phone });
    setOpen(false);
    setQuery("");
  };

  const handlePhoneChange = (e) => {
    // Keep just digits + a couple of allowed separators for UX. The stored
    // value is what the user typed (we don't strip separators aggressively
    // so paste from other apps still works).
    const val = e.target.value;
    onChange?.({ country, phone: val });
  };

  return (
    <div className="flex gap-2" data-testid={`${testIdPrefix}-wrapper`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-10 shrink-0 min-w-[7rem] justify-between px-3 gap-2 font-medium"
            data-testid={`${testIdPrefix}-country-btn`}
          >
            <span className="flex items-center gap-2">
              <span className="text-base leading-none">{current.flag}</span>
              <span className="text-sm">+{current.dial}</span>
            </span>
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-72" data-testid={`${testIdPrefix}-country-popover`}>
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code…"
                className="pl-8 h-9"
                data-testid={`${testIdPrefix}-country-search`}
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="text-xs text-muted-foreground px-3 py-4 text-center">No countries match "{query}"</div>
            )}
            {filtered.map((c) => {
              const active = c.code === country;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => pickCountry(c)}
                  data-testid={`${testIdPrefix}-country-option-${c.code}`}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors",
                    active && "bg-muted"
                  )}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="flex-1 truncate text-left">{c.name}</span>
                  <span className="text-xs text-muted-foreground">+{c.dial}</span>
                  {active && <Check className="w-3.5 h-3.5 text-primary" />}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <Input
        value={phone}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        inputMode="tel"
        autoComplete="tel-national"
        className="h-10"
        data-testid={`${testIdPrefix}-number`}
      />
    </div>
  );
};

export default CountryPhone;
